import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { ActivityCategory, ActivityEntry } from '../models/ActivityCategory';

/**
 * Non-billable time tracking. An employee either clocks in/out for an activity
 * (open -> closed) or logs a completed block manually. Stored in the top-level
 * `activityEntries` collection. Queries filter by rbtId (single-field, no index)
 * and refine in memory, matching the app's existing pattern.
 */
class ActivityService {
  private col = collection(db, 'activityEntries');

  /** Start a clock for an activity. Returns the new entry id. */
  async clockIn(
    companyId: string,
    rbtId: string,
    rbtName: string,
    category: ActivityCategory,
    notes?: string
  ): Promise<string> {
    const now = Timestamp.now();
    const ref = await addDoc(this.col, {
      companyId,
      rbtId,
      rbtName,
      category,
      ...(notes ? { notes } : {}),
      startTime: now,
      status: 'open',
      createdAt: now,
      updatedAt: now,
    });
    return ref.id;
  }

  /** Close an open clock; computes minutes from start to now. */
  async clockOut(entryId: string, startTime: Timestamp): Promise<void> {
    const end = Timestamp.now();
    const minutes = Math.max(0, Math.round((end.toMillis() - startTime.toMillis()) / 60000));
    await updateDoc(doc(this.col, entryId), {
      endTime: end,
      minutes,
      status: 'closed',
      updatedAt: end,
    });
  }

  /** Log a completed activity block manually (no live clock). */
  async addManual(
    companyId: string,
    rbtId: string,
    rbtName: string,
    category: ActivityCategory,
    date: Date,
    minutes: number,
    notes?: string
  ): Promise<string> {
    const start = Timestamp.fromDate(date);
    const end = Timestamp.fromDate(new Date(date.getTime() + minutes * 60000));
    const now = Timestamp.now();
    const ref = await addDoc(this.col, {
      companyId,
      rbtId,
      rbtName,
      category,
      ...(notes ? { notes } : {}),
      startTime: start,
      endTime: end,
      minutes,
      status: 'closed',
      createdAt: now,
      updatedAt: now,
    });
    return ref.id;
  }

  /** The employee's currently-open clock, if any. */
  async getOpenEntry(rbtId: string): Promise<ActivityEntry | null> {
    const snap = await getDocs(query(this.col, where('rbtId', '==', rbtId)));
    const open = snap.docs
      .map((d) => ({ id: d.id, ...d.data() } as ActivityEntry))
      .filter((e) => e.status === 'open')
      .sort((a, b) => b.startTime.toMillis() - a.startTime.toMillis());
    return open[0] || null;
  }

  /** Closed activity entries for an RBT within a period (newest first). */
  async getByRbtAndPeriod(rbtId: string, periodStart: Date, periodEnd: Date): Promise<ActivityEntry[]> {
    const snap = await getDocs(query(this.col, where('rbtId', '==', rbtId)));
    const startMs = periodStart.getTime();
    const endMs = periodEnd.getTime();
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() } as ActivityEntry))
      .filter((e) => e.status === 'closed' && e.startTime.toMillis() >= startMs && e.startTime.toMillis() <= endMs)
      .sort((a, b) => b.startTime.toMillis() - a.startTime.toMillis());
  }
}

export const activityService = new ActivityService();
